import React, { useEffect, useState } from 'react';
import { LabelChip, LabelPickerPopover, parseLabelIds } from './LabelPicker';

// Every visual/box-model property here is set via inline `style`, not a
// CSS className — see LabelPicker.js's note on why. This sidebar is new
// and has never been battle-tested against the host page's stylesheet,
// so it gets the same bulletproof treatment from day one instead of
// waiting to discover the same bug a fourth time.

const DEFAULT_FIELDS = [
  { key: 'deal_stage', label: 'Deal Stage', type: 'select', options: ['New', 'Qualified', 'Negotiating', 'Won', 'Lost'] },
  { key: 'company',    label: 'Company',    type: 'text' },
  { key: 'notes',      label: 'Notes',      type: 'textarea' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(d)) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function fullDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(d)) return '—';
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const EVENT_LABELS = {
  resolved:               { icon: '✓', text: 'Conversation resolved',        color: '#00966b' },
  reopened:                { icon: '↻', text: 'Conversation reopened',        color: '#2563eb' },
  assigned:                { icon: '👤', text: 'Assigned to agent',            color: '#7c3aed' },
  unassigned:              { icon: '↩', text: 'Agent removed',                color: '#6b7280' },
  handed_to_agent_queue:   { icon: '⏳', text: 'Sent to agent queue',          color: '#d97706' },
  handed_to_bot:           { icon: '🤖', text: 'Handed back to bot',           color: '#6b7280' },
};

function EventRow({ event }) {
  const meta = EVENT_LABELS[event.cvEvent] || { icon: '•', text: event.cvEvent, color: '#6b7280' };
  return (
    <li style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f2f4' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, minWidth: 22, borderRadius: '50%',
        background: `${meta.color}1a`, color: meta.color, fontSize: 11, flexShrink: 0, marginTop: 1,
      }}>
        {meta.icon}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1f2937' }}>
          {meta.text}{event.cvMeta ? ` — ${event.cvMeta}` : ''}
        </div>
        <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 1 }}>{fullDate(event.cvCreatedAt)}</div>
      </div>
    </li>
  );
}

function Field({ def, value, onSave }) {
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const dirty = draft !== (value ?? '');

  async function commit() {
    if (!dirty) return;
    setSaving(true);
    try {
      await onSave(def.key, draft);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } finally { setSaving(false); }
  }

  const inputBase = {
    width: '100%',
    boxSizing: 'border-box',
    border: `1.5px solid ${dirty ? '#00C88C' : '#e5e7eb'}`,
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 12.5,
    fontFamily: 'inherit',
    color: '#1f2937',
    background: '#fff',
    outline: 'none',
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <label style={{ fontSize: 10.5, fontWeight: 700, color: '#8a94a3', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {def.label}
        </label>
        {saving && <span style={{ fontSize: 9.5, color: '#9ca3af' }}>saving…</span>}
        {!saving && justSaved && <span style={{ fontSize: 9.5, color: '#00966b', fontWeight: 700 }}>✓ saved</span>}
      </div>
      {def.type === 'select' ? (
        <select
          value={draft}
          onChange={e => { setDraft(e.target.value); onSave(def.key, e.target.value); }}
          style={{ ...inputBase, cursor: 'pointer' }}
        >
          <option value="">—</option>
          {def.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : def.type === 'textarea' ? (
        <>
          <textarea
            value={draft}
            rows={3}
            placeholder={`Add ${def.label.toLowerCase()}…`}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            style={{ ...inputBase, resize: 'vertical', lineHeight: 1.4 }}
          />
          {dirty && (
            <button
              type="button"
              onClick={commit}
              disabled={saving}
              style={{
                marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 14px', borderRadius: 7, border: 'none',
                background: '#00C88C', color: '#fff', fontSize: 11.5, fontWeight: 700,
                cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
              }}
            >
              {saving ? 'Saving…' : '✓ Save'}
            </button>
          )}
        </>
      ) : (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            value={draft}
            placeholder={`Add ${def.label.toLowerCase()}…`}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
            style={{ ...inputBase, flex: 1, minWidth: 0 }}
          />
          {dirty && (
            <button
              type="button"
              onClick={commit}
              disabled={saving}
              title="Save"
              aria-label="Save"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, minWidth: 30, borderRadius: 8, border: 'none',
                background: '#00C88C', color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: saving ? 'default' : 'pointer', flexShrink: 0, boxSizing: 'border-box',
                WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
              }}
            >
              {saving ? '…' : '✓'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ContactSidebar({
  loading,
  contact,        // bot_users row, or null
  fields,         // { [key]: value }
  events,         // bot_conversation_events rows
  eventsLoading,
  chatId,
  convName,
  convLabels,     // cLabels string off the conversation
  allLabels,      // full label definitions list
  onToggleLabel,
  onSaveField,
  onClose,        // mobile back button
  fullWidth,      // true on mobile — take the whole panel instead of a fixed 300px column
}) {
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const selectedLabelIds = parseLabelIds(convLabels);
  const activeLabels = selectedLabelIds.map(id => allLabels.find(l => l.lId === id)).filter(Boolean);

  return (
    <aside style={{
      width: fullWidth ? '100%' : 300,
      minWidth: fullWidth ? 0 : 260,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      height: '100%',
      overflow: 'hidden',
      borderLeft: fullWidth ? 'none' : '1.5px solid #e5e7eb',
      background: '#fff',
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
        padding: '12px 14px', borderBottom: '1.5px solid #e5e7eb', boxSizing: 'border-box',
      }}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close contact details"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 26, height: 26, minWidth: 26, borderRadius: '50%', border: '1.5px solid #e5e7eb',
              background: '#fff', color: '#6b7280', fontSize: 12, cursor: 'pointer', boxSizing: 'border-box',
              WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
            }}
          >
            ✕
          </button>
        )}
        <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>Contact details</div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 14px', boxSizing: 'border-box' }}>
        {loading ? (
          <div style={{ fontSize: 12.5, color: '#9ca3af', textAlign: 'center', padding: '30px 0' }}>Loading…</div>
        ) : (
          <>
            {/* Identity */}
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', margin: '0 auto 10px',
                background: '#00C88C', color: '#fff', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 22, fontWeight: 800, boxSizing: 'border-box',
              }}>
                {(convName || chatId || '?')[0]?.toUpperCase()}
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{convName || chatId}</div>
              <div style={{ fontSize: 12, color: '#8a94a3', marginTop: 2 }}>{chatId}</div>
              {!!contact?.uIsBlocked && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8,
                  padding: '2px 10px', borderRadius: 999, background: '#fee2e2', color: '#b91c1c',
                  fontSize: 10.5, fontWeight: 700,
                }}>
                  🚫 Blocked
                </div>
              )}
            </div>

            {/* Quick stats */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18,
            }}>
              <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 12px', boxSizing: 'border-box' }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>First seen</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1f2937', marginTop: 2 }}>{timeAgo(contact?.uCreatedAt)}</div>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 12px', boxSizing: 'border-box' }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Last seen</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1f2937', marginTop: 2 }}>{timeAgo(contact?.uLastSeenAt)}</div>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 12px', boxSizing: 'border-box', gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Messages sent</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1f2937', marginTop: 2 }}>{contact?.uMsgCount ?? '—'}</div>
              </div>
            </div>

            {/* Labels */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#8a94a3', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
                Labels
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                {activeLabels.map(l => (
                  <LabelChip key={l.lId} label={l} onRemove={() => onToggleLabel(l.lId)} />
                ))}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setLabelPickerOpen(o => !o)}
                    style={{
                      padding: '3px 10px', borderRadius: 999, border: '1.5px dashed #cbd5e1',
                      background: '#fff', fontSize: 11, fontWeight: 700, color: '#6b7280',
                      cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    }}
                  >
                    + Label
                  </button>
                  {labelPickerOpen && (
                    <LabelPickerPopover
                      labels={allLabels}
                      selectedIds={selectedLabelIds}
                      onToggle={onToggleLabel}
                      onClose={() => setLabelPickerOpen(false)}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Custom fields (the CRM part) */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#8a94a3', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
                Details
              </div>
              {DEFAULT_FIELDS.map(def => (
                <Field key={def.key} def={def} value={fields?.[def.key]} onSave={onSaveField} />
              ))}
            </div>

            {/* History timeline */}
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#8a94a3', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
                History
              </div>
              {eventsLoading ? (
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Loading…</div>
              ) : !events || events.length === 0 ? (
                <div style={{ fontSize: 12, color: '#9ca3af' }}>No activity recorded yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {events.map((e, i) => <EventRow key={i} event={e} />)}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}